import React, { useEffect, useState } from 'react';
import { Alert, Card, Col, Form, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment';
import axios from 'helpers/api';
import AdvanceTableViewPatient from 'components/common/advance-table/AdvanceTableViewPatient.js';
import AdvanceTableSearchBox from 'components/common/advance-table/AdvanceTableSearchBox';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import useAdvanceTable from 'hooks/useAdvanceTable';
import ShimmerUI from 'components/ShimmerUI/PatientShimmer';
import CommonBreadCrumb from 'components/commonbreadcrumb/CommonBreadCrumb';
import paths from 'routes/paths';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import RefillDiscontinuedModal from 'components/prescription/RefillDiscontinuedModal';
import IconItem from 'components/common/icon/IconItem';
import AddOrderModal from 'components/patient/view-patient/AddOrderModal';
import SetMastersDetails from 'components/common/SetMastersDetails';

const formatDob = (dob) => {
  if (!dob) return '';
  const m = moment(dob, ['YYYY-MM-DD', moment.ISO_8601, 'MM/DD/YYYY', 'MM/DD/YY'], true);
  return m.isValid() ? m.format('MM/DD/YY') : '';
};

const getGenderName = (gender) => {
  if (gender === 1 || gender === '1') return 'Male';
  if (gender === 2 || gender === '2') return 'Female';
  return 'Other';
};

/**
 * Normalize an `/athome/my-patients` (or `/athome/patients/:user_id`) response
 * into the row shape used by the caregiver table. Mirrors the field set built
 * by `ViewPatient.fetchPatientData` so the same table component can render it.
 */
export const normalizeAtHomePatients = (data) => {
  const list = data?.patients?.data || data?.data || data?.patients || [];
  return (Array.isArray(list) ? list : [])
    .map((value) => {
      const firstName = value.first_name || '';
      const lastName = value.last_name || '';
      const genderName = getGenderName(value.gender);
      // UI-073: SL-110 surfaces nurse + case-manager (name + phone) per row.
      // Build display-ready name strings only when at least one of first/last
      // is present.
      const nurseName =
        [value.nurse_first_name, value.nurse_last_name].filter(Boolean).join(' ').trim();
      const cmName =
        [value.case_manager_first_name, value.case_manager_last_name].filter(Boolean).join(' ').trim();
      return {
        id: value.id || value.patient_id,
        patientid: value.id || value.patient_id,
        individual: `${lastName}, ${firstName}`,
        name: `${lastName}, ${firstName}`,
        first_name: firstName,
        last_name: lastName,
        dob: formatDob(value.dob),
        gender: genderName,
        facilityid: value.facility_id,
        facilityname: value.facility_name || '',
        facility: value.facility_name || '',
        relationship: value.relationship || '',
        allergies: value.allergies || '',
        is_active: value.is_active,
        nurse_name: nurseName,
        nurse_phone: value.nurse_phone || '',
        case_manager_name: cmName,
        case_manager_phone: value.case_manager_phone || ''
      };
    })
    .sort((a, b) => (a.last_name || '').localeCompare(b.last_name || ''));
};

/**
 * UI-034: caregiver's patient list, sourced LIVE from /athome/my-patients
 * (backend SL-019 — never the JWT cache). Renders the same table component
 * that powers /main/patient/view-patient (`AdvanceTableViewPatient`) so the
 * caregiver experience matches facility staff visually.
 *
 * Differences vs ViewPatient: no Group/Facility selectors (caregivers see only
 * their assigned patients); no Add Order column (caregivers can't place orders).
 */
const MyPatientsList = () => {
  const [patient, setPatient] = useState([]);
  const [masterPatient, setMasterPatient] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRefillModal, setShowRefillModal] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    // For caregivers, "Active" / "Inactive" are redefined: Active = currently
    // assigned to me; Inactive = no longer assigned (unassigned or discharged).
    // We compute that client-side by diffing the historical-inclusive endpoint
    // (backend SL-019a) against the currently-active list (SL-019).
    const fetchMyPatients = async () => {
      setLoading(true);
      try {
        const tokenStr = localStorage.getItem('accessToken');
        const headers = { Authorization: `Bearer ${tokenStr}` };

        const [allRes, activeRes] = await Promise.all([
          axios.get(
            `${process.env.REACT_APP_SERVER_URL}/athome/my-patients?include_historical=true`,
            { headers }
          ),
          axios.get(
            `${process.env.REACT_APP_SERVER_URL}/athome/my-patients`,
            { headers }
          )
        ]);

        const activeRows = normalizeAtHomePatients(activeRes.data);
        const activeIds = new Set(activeRows.map((p) => String(p.id)));
        const allRows = normalizeAtHomePatients(allRes.data).map((p) => ({
          ...p,
          // Re-purpose is_active to mean "currently assigned to me".
          is_active: activeIds.has(String(p.id))
        }));

        setMasterPatient(allRows);
        setPatient(allRows.filter((p) => p.is_active));  
      } catch (error) {
        setMasterPatient([]);
        setPatient([]);
        toast.error('Unable to load your patients');
      } finally {
        setLoading(false);
      }
    };
    SetMastersDetails();
    fetchMyPatients();
  }, []);

  const handlefilterChange = (e) => {
    const value = e.target.value;
    if (value === 'active') {
      setPatient(masterPatient.filter((p) => p.is_active));
    } else if (value === 'inactive') {
      setPatient(masterPatient.filter((p) => !p.is_active));
    } else {
      setPatient(masterPatient);
    }
  };

  const addOrderModal = () => {
        setShowModal(true);
    };

  const columns = [
    {
      accessorKey: 'individual',
      header: 'Individual',
      meta: {
        headerProps: { className: 'text-900 text-start' },
        cellProps: { className: 'text-start' }
      },
      cell: ({ row: { original } }) => (
        <Link to={`${paths.patientDetails}/${original.patientid}`}>
          {original.individual}
        </Link>
      )
    },
    // {
    //   accessorKey: 'dob',
    //   header: 'Dob',
    //   meta: {
    //     headerProps: { className: 'text-900 text-start' },
    //     cellProps: { className: 'text-start' }
    //   }
    // },
    // {
    //   accessorKey: 'gender',
    //   header: 'Gender',
    //   meta: {
    //     headerProps: { className: 'text-900 text-start' },
    //     cellProps: { className: 'text-start' }
    //   }
    // },
    // {
    //   accessorKey: 'facilityname',
    //   header: 'Facility Name',
    //   meta: {
    //     headerProps: { className: 'text-900 text-start' },
    //     cellProps: { className: 'text-start' }
    //   }
    // },
    // {
    //   accessorKey: 'relationship',
    //   header: 'Relationship',
    //   meta: {
    //     headerProps: { className: 'text-900 text-start' },
    //     cellProps: { className: 'text-start text-capitalize' }
    //   },
    //   cell: ({ row: { original } }) =>
    //     original.relationship ? original.relationship : '—'
    // },
    {
      accessorKey: 'nurse_name',
      header: 'Nurse',
      meta: {
        headerProps: { className: 'text-900 text-start' },
        cellProps: { className: 'text-start' }
      },
      // UI-073: nurse name + click-to-call phone. SL-110 surfaces these
      // fields on each /athome/my-patients row.
      cell: ({ row: { original } }) => {
        if (!original.nurse_name) return <span className="text-400">—</span>;
        return (
          <div className="text-start">
            <div>{original.nurse_name}</div>
            {original.nurse_phone && (
              <a href={`tel:${original.nurse_phone}`} className="fs-11 text-decoration-none">
                {original.nurse_phone}
              </a>
            )}
          </div>
        );
      }
    },
    {
      accessorKey: 'case_manager_name',
      header: 'Case Manager',
      meta: {
        headerProps: { className: 'text-900 text-start' },
        cellProps: { className: 'text-start' }
      },
      cell: ({ row: { original } }) => {
        if (!original.case_manager_name) return <span className="text-400">—</span>;
        return (
          <div className="text-start">
            <div>{original.case_manager_name}</div>
            {original.case_manager_phone && (
              <a href={`tel:${original.case_manager_phone}`} className="fs-11 text-decoration-none">
                {original.case_manager_phone}
              </a>
            )}
          </div>
        );
      }
    },
    {
      accessorKey: 'addorder',
      header: 'Add Order',
      meta: {
        headerProps: { className: 'text-900 text-start w-10' }
      },
      cell: ({ row: { original } }) => {
        return <Link state={{
          triggeredBy: "someAction",
          data: original
        }}>
          <IconItem
            tag="button"
            icon={['far', 'edit']}
            size="sm"
            onClick={addOrderModal}
            className="btn rounded-3 me-2 fs-11 align-items-center"
          />
        </Link>
      }
    },
    {
      accessorKey: '',
      header: 'Refill',
      meta: {
        headerProps: { className: 'text-900 text-start' },
        cellProps: { className: 'text-start' }
      },
      cell: ({ row: { original } }) => {
        return <Link className="p-2" onClick={() => { setShowRefillModal(true) }}
          state={{
            triggeredBy: "someAction",
            id: original.patientid,
            dataObj: original
          }}>
          <FontAwesomeIcon icon="plus" className="me-1 fs-11" /></Link>
      }
    }
  ];

  const table = useAdvanceTable({
    data: patient,
    columns,
    selection: false,
    sortable: true,
    pagination: true
  });

  const breadcrumbItems = [
    { name: 'Dashboard', href: '/main', isActive: false },
    { name: 'My Patients', href: paths.caregiverMyPatients, isActive: true }
  ];

  return (
    <>
      <CommonBreadCrumb breadcrumbItems={breadcrumbItems} />
      {showRefillModal && <RefillDiscontinuedModal show={showRefillModal} setShow={setShowRefillModal} />}
      {showModal && <AddOrderModal show={showModal} setShow={setShowModal} />}
      <Card>
        <Card.Body className="p-3">
          <Row className="align-items-end mb-3">
            <Col xs="9" className="mb-0 view-patient-search-holder">
              <AdvanceTableProvider {...table}>
                <AdvanceTableSearchBox placeholder="Search..." />
              </AdvanceTableProvider>
            </Col>
            <Col xs="3" className="mb-0">
              <Form.Label>Filter</Form.Label>
              <Form.Select
                name="sortby"
                onChange={handlefilterChange}
                defaultValue="active"
              >
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {loading ? (
        <ShimmerUI />
      ) : patient.length > 0 ? (
        <AdvanceTableProvider {...table}>
          <Card className="mb-3">
            <Card.Body className="p-3">
              <Row>
                <Col xs="12" className="mb-1 mt-1">
                  <AdvanceTableViewPatient
                    headerClassName="bg-200 text-nowrap align-middle text-center"
                    rowClassName="text-nowrap align-middle text-center"
                    tableProps={{
                      size: 'sm',
                      striped: true,
                      className: 'fs-10 mb-0 overflow-hidden'
                    }}
                  />
                </Col>
              </Row>
            </Card.Body>
            <Card.Footer>
              <div className="mt-3">
                <AdvanceTableFooter rowInfo navButtons rowsPerPageSelection />
              </div>
            </Card.Footer>
          </Card>
        </AdvanceTableProvider>
      ) : (
        <Alert variant="warning" className="text-center">
          <h5 className="mb-0">No Patients Found</h5>
        </Alert>
      )}
    </>
  );
};

export default MyPatientsList;
